<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ArrowLeft, ArrowRight, CheckCircle2, LoaderCircle, RotateCcw, Volume2 } from 'lucide-vue-next';
import { useRouter } from 'vue-router';
import AudioRecorder from '@/components/AudioRecorder.vue';
import { cl_api, cl_errorMessage } from '@/api';
import { useLearnerStore } from '@/stores/learner';
import type { ClAssessment, ClReviewItem } from '@/types';

const cl_learner = useLearnerStore();
const cl_router = useRouter();
const cl_items = ref<ClReviewItem[]>([]);
const cl_index = ref(0);
const cl_blob = ref<Blob | null>(null);
const cl_result = ref<ClAssessment | null>(null);
const cl_status = ref<'idle' | 'uploading' | 'analyzing'>('idle');
const cl_loading = ref(true);
const cl_error = ref('');
const cl_playing = ref(false);
const cl_sessionId = ref('');
let cl_audio: HTMLAudioElement | null = null;

const cl_current = computed(() => cl_items.value[cl_index.value] ?? null);
const cl_progress = computed(() => cl_items.value.length ? ((cl_index.value + 1) / cl_items.value.length) * 100 : 0);
const cl_resultLabel = computed(() => ({ MASTERED: 'Great!', GOOD: 'Nice work!', PRACTICE: 'Almost!', NEEDS_WORK: 'Try again!' }[cl_result.value?.cl_status ?? ''] ?? ''));

onMounted(async () => {
  try {
    cl_items.value = (await cl_api.get('/learning/review', { params: { cl_childId: cl_learner.cl_activeChildId } })).data;
    if (cl_items.value.length) {
      cl_sessionId.value = (await cl_api.post('/learning/sessions', { cl_childId: cl_learner.cl_activeChildId, cl_type: 'REVIEW' })).data.id;
    }
  } catch (cl_cause) {
    cl_error.value = cl_errorMessage(cl_cause);
  } finally {
    cl_loading.value = false;
  }
});

onBeforeUnmount(() => {
  cl_audio?.pause();
  window.speechSynthesis?.cancel();
});

async function cl_playStandard() {
  if (!cl_current.value || cl_playing.value) return;
  cl_playing.value = true;
  cl_error.value = '';
  try {
    const cl_response = await cl_api.get('/pronunciation/tts', { params: { cl_childId: cl_learner.cl_activeChildId, cl_wordId: cl_current.value.word.id }, responseType: 'blob' });
    const cl_url = URL.createObjectURL(cl_response.data);
    cl_audio = new Audio(cl_url);
    cl_audio.onended = () => { cl_playing.value = false; URL.revokeObjectURL(cl_url); };
    cl_audio.onerror = () => { cl_playing.value = false; URL.revokeObjectURL(cl_url); cl_error.value = '标准发音播放失败，请稍后重试。'; };
    await cl_audio.play();
  } catch {
    if (!cl_speak(cl_current.value.word.text)) {
      cl_playing.value = false;
      cl_error.value = '当前设备无法播放标准发音。';
    }
  }
}

function cl_speak(cl_text: string) {
  if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) return false;
  const cl_utterance = new SpeechSynthesisUtterance(cl_text);
  cl_utterance.lang = cl_learner.cl_activeChild?.profile.accent === 'EN_GB' ? 'en-GB' : 'en-US';
  cl_utterance.rate = 0.82;
  cl_utterance.onend = () => { cl_playing.value = false; };
  cl_utterance.onerror = () => { cl_playing.value = false; };
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(cl_utterance);
  return true;
}

function cl_recorded(cl_recording: Blob) {
  cl_blob.value = cl_recording;
  cl_result.value = null;
  cl_error.value = '';
}

async function cl_analyze() {
  if (!cl_blob.value || !cl_current.value) return;
  cl_status.value = 'uploading';
  cl_error.value = '';
  try {
    const cl_form = new FormData();
    cl_form.append('cl_audio', cl_blob.value, `review.${cl_blob.value.type.includes('mp4') ? 'm4a' : 'webm'}`);
    cl_form.append('cl_childId', cl_learner.cl_activeChildId);
    cl_form.append('cl_wordId', cl_current.value.word.id);
    cl_form.append('cl_reviewItemId', cl_current.value.id);
    cl_form.append('cl_sessionId', cl_sessionId.value);
    window.setTimeout(() => { if (cl_status.value === 'uploading') cl_status.value = 'analyzing'; }, 300);
    cl_result.value = (await cl_api.post('/pronunciation/assess', cl_form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 45_000 })).data;
  } catch (cl_cause) {
    cl_error.value = cl_errorMessage(cl_cause);
  } finally {
    cl_status.value = 'idle';
  }
}

function cl_next() {
  cl_index.value += 1;
  cl_blob.value = null;
  cl_result.value = null;
  cl_error.value = '';
}

async function cl_finish() {
  if (cl_sessionId.value) await cl_api.patch(`/learning/sessions/${cl_sessionId.value}/complete`, { cl_childId: cl_learner.cl_activeChildId });
  await cl_router.push('/');
}
</script>

<template>
  <section class="cl-practice-page">
    <header class="cl-practice-header"><RouterLink to="/" class="cl-icon-button" title="返回首页"><ArrowLeft /></RouterLink><div><span>今日复习</span><div class="cl-progress"><i :style="{ width: `${cl_progress}%` }" /></div></div><strong>{{ cl_items.length ? `${cl_index + 1}/${cl_items.length}` : '' }}</strong></header>
    <p v-if="cl_error" class="cl-form-error cl-practice-error">{{ cl_error }}</p>
    <div v-if="cl_loading" class="cl-empty-practice"><LoaderCircle class="cl-spin" /><p>正在安排复习内容...</p></div>
    <div v-else-if="!cl_current" class="cl-empty-practice"><CheckCircle2 /><h1>今天的复习完成了</h1><p>新的复习任务会根据掌握情况自动安排。</p><RouterLink class="cl-primary-button" to="/map">继续学习</RouterLink></div>
    <div v-else class="cl-practice-stage cl-review-stage">
      <aside class="cl-teaching-panel"><p class="cl-eyebrow">Review focus</p><div class="cl-sound-pair"><strong>{{ cl_current.phoneme?.symbol ?? cl_current.word.text[0] }}</strong><span>/{{ cl_current.phoneme?.symbol ?? 'word' }}/</span></div><p>{{ cl_current.phoneme?.correctionPrompt ?? '先认真听，再清楚地读出这个单词。' }}</p><dl><dt>复习次数</dt><dd>第 {{ cl_current.repetitions + 1 }} 轮</dd><dt>难度</dt><dd>{{ cl_current.word.difficulty }}</dd></dl></aside>
      <main class="cl-word-stage"><span class="cl-word-meaning">{{ cl_current.word.meaningZh }}</span><h1>{{ cl_current.word.text.toUpperCase() }}</h1><p class="cl-word-ipa">{{ cl_current.word.ipaUs }}</p><button class="cl-listen-button" :disabled="cl_playing" @click="cl_playStandard"><LoaderCircle v-if="cl_playing" class="cl-spin" /><Volume2 v-else /> Listen</button><div class="cl-phoneme-row"><div v-for="cl_part in cl_current.word.phonemes" :key="cl_part.id"><strong>{{ cl_part.grapheme }}</strong><span>/{{ cl_part.phoneme.symbol }}/</span></div></div><AudioRecorder :key="`${cl_current.id}:${cl_result?.cl_attemptId ?? ''}`" :cl_max-seconds="5" @cl_ready="cl_recorded" @cl_error="cl_error = $event" /><button v-if="cl_blob && !cl_result" class="cl-primary-button cl-analyze-button" :disabled="cl_status !== 'idle'" @click="cl_analyze"><LoaderCircle v-if="cl_status !== 'idle'" class="cl-spin" />{{ cl_status === 'uploading' ? 'Uploading...' : cl_status === 'analyzing' ? 'Analyzing...' : '分析发音' }}</button></main>
      <aside v-if="cl_result" class="cl-result-panel"><div class="cl-result-score"><RotateCcw /><span>{{ cl_resultLabel }}</span><strong v-if="cl_learner.cl_activeChild?.profile.parentModeNumbers">{{ Math.round(cl_result.cl_systemScore) }}</strong></div><div class="cl-feedback-list"><div v-for="(cl_item, cl_feedbackIndex) in cl_result.cl_feedback" :key="`${cl_item.cl_phoneme}-${cl_feedbackIndex}`" :class="`status-${cl_item.cl_status.toLowerCase()}`"><span>/{{ cl_item.cl_phoneme }}/</span><strong>{{ cl_item.cl_status === 'MASTERED' ? 'Excellent' : cl_item.cl_status === 'GOOD' ? 'Great' : 'Practice again' }}</strong><small>{{ cl_item.cl_tip }}</small></div></div><button v-if="cl_index < cl_items.length - 1" class="cl-primary-button" @click="cl_next">下一个 <ArrowRight /></button><button v-else class="cl-primary-button" @click="cl_finish">完成复习 <CheckCircle2 /></button></aside>
    </div>
  </section>
</template>
